<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="localFarms"
      hide-default-footer
      :server-items-length="totalFarms"
      item-key="id"
      @item-selected="handleSelect"
      @toggle-select-all="handleSelectAll"
      :show-select="true"
    >
      <template v-slot:top="{}">
        <div class="d-flex">
          <v-text-field
            prepend-inner-icon="mdi-magnify"
            style="width: 150px"
            outlined
            height="5px"
            :placeholder="$t('search')"
            dense
            v-model="search"
            @input="handleSearchByPharse"
            class="shrink"
            clearable
          ></v-text-field>

          <div class="d-inline-flex ml-3">
            <v-autocomplete
              outlined
              dense
              v-model="options.country"
              :items="countries"
              item-value="name"
              item-text="name"
              :placeholder="$t('deforestation.allCountries')"
              class="mr-3"
              return-object
              @change="getState"
              clearable
              v-on:click:clear="handleClearCountry"
            >
            </v-autocomplete>
            <v-autocomplete
              v-if="toggleFilterState"
              outlined
              dense
              v-model="options.state"
              :items="states"
              :placeholder="$t('provinceState')"
              return-object
              @change="getStateData"
              v-on:click:clear="handleClearState"
            >
            </v-autocomplete>
          </div>
          <v-menu
            v-model="filterByDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                append-icon="mdi-calendar"
                style="width: 150px"
                v-model="dateRangeText"
                class="ml-2"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                clearable
                v-on:click:clear="handleClearDate"
                v-on:click:append="filterByDateMenu = true"
                :placeholder="$t('oma.filterByDate')"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="options.date"
              range
              @change="handleChangeDate"
            ></v-date-picker>
          </v-menu>
          <v-autocomplete
              outlined
              dense
              v-model="selectedSociety"
              :items="societyList"
              item-value="id"
              item-text="name"
              :placeholder="$t('farm.factory')"
              class="mx-2"
              return-object
              clearable
              @change="handleSocietyChange"
              v-on:click:clear="handleClearSociety"
            >
            </v-autocomplete>
            <!-- v select for farm created from -->
            <v-select
              outlined
              dense
              v-model="options.farm_created_from"
              :items="farmCreatedFromOptions"
              item-value="value"
              item-text="text"
              :placeholder="$t('farm.farmAddedBy')"
              class="mx-2"
              clearable
              @change="handleFarmCreatedFromChange"
              v-on:click:clear="handleClearFarmCreatedFrom"
              multiple
            >
            </v-select>

          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            height="40"
            class="mr-4 px-4"
            outlined
            @click="downloadCsv"
            small
          >
            <v-img width="15px" src="/icons/pngfile.png"></v-img>
            <span class="ml-2">{{ $t("farm.exportAsCSV") }}</span>
          </v-btn>
          <div class="py-1 title">
            {{ from }} - {{ to }} {{ $t("of") }}
            {{ totalFarms }}
          </div>
          <v-btn
            class="mx-2"
            fab
            small
            outlined
            color="primary"
            :disabled="options.page <= 1"
            @click="pageChange(false)"
          >
            <v-icon dark> mdi-chevron-left </v-icon>
          </v-btn>
          <v-btn
            class="mx-2"
            fab
            small
            outlined
            color="primary"
            :disabled="options.page >= totalPages"
            @click="pageChange(true)"
          >
            <v-icon dark> mdi-chevron-right </v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:item.farmName="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text"
          @click="viewFarm(item.id, 0)"
        >
          {{ item.farmName || "" }}
        </div>
      </template>
      <template v-slot:item.farmerName="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text"
          @click="
            $router.push({ name: 'farmers_view', params: { id: item.user.id } })
          "
        >
          {{
            item.user
              ? item.user.firstName +
                (item.user.middleName ? " " + item.user.middleName : "") +
                (item.user.lastName ? " " + item.user.lastName : "")
              : item.farmerFirstName
              ? item.farmerFirstName +
                (item.farmerMiddleName ? " " + item.farmerMiddleName : "") +
                (item.farmerLastName ? " " + item.farmerLastName : "")
              : ""
          }}
        </div>
      </template>
      <template v-slot:item.farmAddress="{ item }">
        {{ decodeHtmlEntities(item.address) || "N/A" }}
      </template>
      <template v-slot:item.registrationNo="{ item }">
        {{ item.registrationNo || "N/A" }}
      </template>
      <template v-slot:item.region="{ item }">
        {{ item.region || "N/A" }}
      </template>
      <template v-slot:item.country="{ item }">
        {{ item.country || "N/A" }}
      </template>
      <template v-slot:item.farmLocation="{ item }">
        {{ item.mainLocation?.state || "N/A" }}
      </template>
      <template v-slot:item.technicianName="{ item }">
        {{ item.technician ? item.technician.fullName : "N/A" }}
      </template>
      <template v-slot:item.size="{ item }">
        {{ item.area ? convertSizeIntoBaseUnit(item.area) : "N/A" }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.createdAt }}
      </template>
      <template v-slot:item.society="{ item }">
        {{ item.societyName ? item.societyName : "N/A" }}
      </template>
      <template v-slot:item.regBy="{ item }">
        <v-tooltip bottom color="black" max-width="350">
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="text-decoration-underline cursor-pointer primary--text"
            >
              {{
                getRegBy(item)
                
              }}
            </div>
          </template>
          <div>
            <div>
              User Id: <b>{{   (item.technician ?  item.technician.id : "") || (item.adminTechnician ?  item.adminTechnician.id : "") || (item.user ? item.user.id : "")}}</b>
            </div>
            <div>
              User Name:
              <b>{{
                   getRegBy(item)
              }}</b>
            </div>
            <div>
              User Role: <b>{{ item.user?.user_role[0]?.name || "N/A" }}</b>
            </div>
          </div>
        </v-tooltip>
        <!-- {{ item.user ? item.user.firstName + " " + item.user.lastName : "" }} -->
      </template>
      <template v-slot:item.status="{ item }">
        <div
          class="text-decoration-underline cursor-pointer primary--text"
          @click="viewFarm(item.id, 2)"
        >
          {{ $t('view') }}
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <div style="right: 10px; top: 10px">
          <v-menu location="start">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :key="item.id" :disabled="!$can($permissions.can_update_farm.value)">
                <v-list-item-title
                  class="cursor-pointer"
                  @click="editFarm(item.id)"
                  :class="{'v-list-item--disabled': !$can($permissions.can_update_farm.value)}"
                  >{{ $t("edit") }}</v-list-item-title
                >
              </v-list-item>
              <v-list-item :disabled="!$can($permissions.can_delete_farm.value)">
                <v-list-item-title
                  class="cursor-pointer"
                  @click="changeFarmStatus(item.id)"
                  :class="{'v-list-item--disabled': !$can($permissions.can_delete_farm.value)}"
                  >{{ $t("delete") }}</v-list-item-title
                >
              </v-list-item>
              <!-- <v-list-item>
                <v-list-item-title>{{
                  $t("farm.generateReport")
                }}</v-list-item-title>
              </v-list-item> -->
            </v-list>
          </v-menu>
        </div>
      </template>
      <template v-slot:footer></template>
    </v-data-table>
    <confirm-box
      :title="confirmElements.title"
      :message="confirmElements.message"
      :cancelBtnText="confirmElements.cancelBtnText"
      :confirmBtnText="confirmElements.confirmBtnText"
      :confirmData="confirmElements.confirmData"
      :dialog="confirm"
      @cancel:action="confirm = false"
      @continue:action="confirmDeactivate"
    ></confirm-box>
  </div>
</template>
<script>
import { getCountries, getStates } from "country-state-picker";
import loadingMixin from "@/mixins/LoadingMixin";
import DownloadMixin from "@/mixins/DownloadMixin";
import MemberDataService from "@/_services/MemberDataService";
import { convertSizeIntoBaseUnit, decodeHtmlEntities } from "@/utils.js";
import _ from "lodash";
import ConfirmBox from "../../../components/ConfirmBox.vue";
import FarmService from "../../../_services/FarmService";
import BuyingStationService from "@/_services/BuyingStationService";
export default {
  components: { ConfirmBox },
  props: {
    headers: {
      type: Array,
    },
    toggleSelection: {
      type: Boolean,
    },
    farms: {
      type: Array,
    },
    totalFarms: {
      type: Number,
    },
    toggleFilterState: {
      type: Boolean,
      default: false,
    }
  },
  watch: {
    farms(newFarm) {
      this.localFarms = newFarm;
    },
  },
  computed: {
    totalPages() {
      return this.totalFarms < this.options.limit
        ? 1
        : Math.ceil(this.totalFarms / this.options.limit);
    },
    dateRangeText() {
      return this.options.date.join(" - ");
    },
    from() {
      return ((this.options.page - 1) * this.options.limit) + 1;
    },
    to() {
      const calculatedTo = this.options.page * this.options.limit;
      return calculatedTo > this.totalFarms ? this.totalFarms : calculatedTo;
    },
    farmCreatedFromOptions() {
      return [
        { value: "mobile", text: this.$t("farm.mobileApp") },
        { value: "admin", text: this.$t("farm.DDSReport") },
        { value: "farm_management", text: this.$t("farm.farmManagement") }
      ];
    },
  },
  data() {
    return {
      localFarms: [],
      search: "",
      selectedFarms: [],
      options: {
        limit: 50,
        page: 1,
        searchPhrase: "",
        country: null,
        state: null,
        date: [],
        status: "",
        orderType: "DESC",
        areaUnitId: JSON.parse(localStorage.getItem("gs"))?.areaUnit.id,
        societyId: null,
        farm_created_from: null,
      },
      countries: [],
      states: [],
      confirm: false,
      confirmElements: {
        title: this.$t("farm.deleteFarm"),
        message: this.$t("farm.confirmDeleteFarm"),
        confirmBtnText: this.$t("addEdituser.activateAct"),
        cancelBtnText: this.$t("cancel"),
        confirmData: {},
      },
      filterByDateMenu: false,
      societyList: [],
      selectedSociety: null,
    };
  },
  created() {
    if(this.$route.params?.id) {
      this.options = {...this.options, userId: this.$route.params?.id}
    }
    this.$emit("fetchFarmData", this.options);
    this.getCountry();
    this.getSocietyList();
  },
  methods: {
    getRegBy(item) {
      if (item.technician) {
        if(item.technician.firstName) {
          return item.technician.firstName + " " + item.technician.lastName;
        } else  {
          return item.technician.email || item.technician.mobile
        }
      } else if (item.adminTechnician) {
        if(item.adminTechnician.firstName) {
          return item.adminTechnician.firstName + " " + item.adminTechnician.lastName;
        } else if (item.adminTechnician.email) {
          return item.adminTechnician.email || item.adminTechnician.mobile;
        }
      } else if (item.user) {
        if(item.user.firstName) {
          return item.user.firstName + " " + item.user.lastName;
        } else if (item.user.email) {
          return item.user.email || item.user.mobile;
        }
      } else {
        return "";
      }
      this.$emit("fetchFarmData", this.options);
    },
    handleFarmCreatedFromChange(farmCreatedFrom) {
      if (farmCreatedFrom) {
        this.options.farm_created_from = farmCreatedFrom;
      } else {
        this.options.farm_created_from = null;
      }
      this.options.page = 1; // Reset to first page when filter changes
      this.$emit("fetchFarmData", this.options);
    },
    handleClearFarmCreatedFrom() {
      this.options.farm_created_from = null;
      this.options.page = 1;
      this.$emit("fetchFarmData", this.options);
    },
    handleClearSociety() {
      this.selectedSociety = null;
      this.options.societyId = null;
      this.options.page = 1;
      this.$emit("fetchFarmData", this.options);
    },
    getSocietyList() {
      BuyingStationService.listBuyingStations({
        page: 1,
        limit: 1000,
        search: "",
        order: "ASC"
      }).then(res => {
        if (res.success && res.data) {
          const arryList = res.data.rows || res.data || [];
          this.societyList = arryList.map(item => ({
            name: item.fullName || `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(),
            id: item.id,
          }));
        }
      }).catch(error => {
        console.error("Error fetching society list:", error);
      });
    },
    handleSocietyChange(society) {
      if (society && society.id) {
        this.options.societyId = society.id;
        this.options.page = 1; // Reset to first page when filter changes
      } else {
        this.options.societyId = null;
      }
      this.$emit("fetchFarmData", this.options);
    },
    decodeHtmlEntities,
    viewFarm(farmId, tabId) {
      this.$router.push({ name: "FarmInfo", params: { farmId, tabId } });
    },
    editFarm(farmId) {
      this.$router.push({ name: "EditFarms", params: { farmId } });
    },
    convertSizeIntoBaseUnit,
    handleSelect({ item, value }) {
      if (value) {
        const alreadySelected = this.selectedFarms.find((farm) => farm.id === item.id);
        if (!alreadySelected) {
          this.selectedFarms = [...this.selectedFarms, item];
        }
      } else {
        this.selectedFarms = this.selectedFarms.filter((farm) => farm.id !== item.id);
      }
    },
    handleSelectAll({ items, value }) {
      if (value) {
        const farmMap = new Map(this.selectedFarms.map((farm) => [farm.id, farm]));
        items.forEach((farm) => farmMap.set(farm.id, farm));
        this.selectedFarms = Array.from(farmMap.values());
      } else {
        const deselectedIds = items.map((farm) => farm.id);
        this.selectedFarms = this.selectedFarms.filter(
          (farm) => !deselectedIds.includes(farm.id)
        );
      }
    },
    getCountry() {
      this.countries = getCountries();
    },
    getState(val) {
      this.states = getStates(val.code);
      this.options.country = val.name;
      this.$emit("fetchFarmData", this.options);
    },
    getStateData(val) {
      this.options.state = val;
      this.$emit("fetchFarmData", this.options);
    },
    handleChangeDate() {
      this.$emit("fetchFarmData", this.options);
      this.filterByDateMenu = false
    },
    handleClearDate() {
      this.options.date = [];
      this.$emit("fetchFarmData", this.options);
    },
    handleClearCountry() {
      this.options.country = null;
      this.$emit("fetchFarmData", this.options);
    },
    handleClearState() {
      this.options.state = null;
      this.$emit("fetchFarmData", this.options);
    },
    pageChange(t) {
      if (t) {
        this.options.page = this.options.page + 1;
      } else {
        this.options.page = this.options.page - 1;
      }
      this.$emit("fetchFarmData", this.options);
    },
    handleSearchByPharse: _.debounce(function () {
      this.options.searchPhrase = this.search;
      this.$emit("fetchFarmData", this.options);
    }, 1000),
    downloadCsv() {
      const csvUrl = MemberDataService.DOWNLOAD_FARMS_CSV_URL;
      const queryParams = new URLSearchParams(this.options);
      
      if (this.selectedFarms && this.selectedFarms.length > 0) {
        const selectedFarmIds = this.selectedFarms.map(farm => farm.id).join(',');
        queryParams.append('farmIds', selectedFarmIds);
      }
      
      const url = `${csvUrl}?${queryParams}`;
      this.startLoading();
      this.downloadSourceFile(url, "text/csv").then(() => {
        this.stopLoading();
      });
    },
    changeFarmStatus(id) {
      this.confirmElements.title = this.$t("farm.deleteFarm");
      (this.confirmElements.message = this.$t("farm.confirmDeleteFarm")),
        (this.confirmElements.confirmBtnText = this.$t("yes"));
      this.confirmElements.confirmData = { farmId: id };
      this.confirm = true;
    },
    async confirmDeactivate(action, confirmData) {
      if (action) {
        await this.startLoading();
        FarmService.deactiveFarm(confirmData.farmId)
          .then(async (res) => {
            if (res.success) {
              await this.stopLoading();
              this.$emit("fetchFarmData", this.options);
              
              this.confirm = false;
              this.$notify({
                title: this.$t("farm.deleted"),
                type: "success",
              });
            } else {
              await this.stopLoading();
              this.confirm = false;
              this.$notify({
                title: this.$t("farm.notDeleted"),
                type: "error",
              });
            }
          })
          .catch(async () => {
            // console.log(err)
            await this.stopLoading();

            this.confirm = false;
          });
      } else {
        this.confirm = false;
      }
    },
  },
  mixins: [loadingMixin, DownloadMixin],
};
</script>
